import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Plus, FileText, Trash2, CalendarClock, Loader2, Pencil,
} from "lucide-react";
import { toast } from "sonner";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [resumes, setResumes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
  }, [authLoading, user, navigate]);

  const load = async () => {
    try {
      const [r, b] = await Promise.all([api.get("/resumes"), api.get("/bookings")]);
      setResumes(r.data);
      setBookings(b.data);
    } catch (e) {
      toast.error("Failed to load your data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const createResume = async () => {
    try {
      const res = await api.post("/resumes", { title: "Untitled Resume", template: "modern", data: {} });
      navigate(`/builder/${res.data.id}`);
    } catch (e) {
      toast.error("Could not create resume");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/resumes/${id}`);
      setResumes((prev) => prev.filter((x) => x.id !== id));
      toast.success("Resume deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (authLoading || loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-5 py-10 font-body" data-testid="dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">Welcome, {user?.name?.split(" ")[0]}</h1>
          <p className="text-muted-foreground">Manage your resumes and consultations.</p>
        </div>
        <Button onClick={createResume} className="rounded-full font-semibold" data-testid="new-resume-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Resume
        </Button>
      </div>

      <h2 className="font-heading font-semibold text-xl mb-4">Your Resumes</h2>
      {resumes.length === 0 ? (
        <Card className="p-12 flex flex-col items-center text-center border-dashed" data-testid="empty-resumes">
          <FileText className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="font-medium">No resumes yet</p>
          <p className="text-sm text-muted-foreground mb-4">Create your first resume to get started.</p>
          <Button onClick={createResume} className="rounded-full" data-testid="empty-new-resume-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Create Resume
          </Button>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {resumes.map((r) => (
            <Card key={r.id} className="p-5 group hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`resume-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground bg-secondary px-2 py-1 rounded">{r.template}</span>
              </div>
              <h3 className="font-heading font-semibold text-lg mt-4 truncate">{r.title}</h3>
              <p className="text-xs text-muted-foreground">Updated {new Date(r.updated_at).toLocaleDateString()}</p>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => navigate(`/builder/${r.id}`)} size="sm" className="rounded-full flex-1" data-testid={`edit-resume-${r.id}`}>
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                </Button>
                <Button onClick={() => remove(r.id)} size="sm" variant="outline" className="rounded-full" data-testid={`delete-resume-${r.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <h2 className="font-heading font-semibold text-xl mt-12 mb-4">Consultation Requests</h2>
      {bookings.length === 0 ? (
        <Card className="p-8 text-center border-dashed" data-testid="empty-bookings">
          <CalendarClock className="h-9 w-9 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No consultations booked yet. <button className="text-primary underline" onClick={() => navigate("/experts")}>Browse experts</button></p>
        </Card>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <Card key={b.id} className="p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`booking-${b.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-accent/15 flex items-center justify-center">
                  <CalendarClock className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="font-semibold">{b.expert_name}</p>
                  <p className="text-xs text-muted-foreground">{b.date} · {b.time_slot}</p>
                </div>
              </div>
              <span className={`text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full ${b.status === "confirmed" ? "bg-green-100 text-green-700" : b.status === "declined" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{b.status}</span>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
